import { createContext, use, useCallback, useMemo, useState } from 'react';
import { Slot } from 'radix-ui';
import { PanelLeft } from 'lucide-react';
import { Button } from './button.jsx';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './sheet.jsx';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip.jsx';
import { useMediaQuery } from '../hooks/use-media-query.js';
import { cn } from '../lib/utils.js';

const STORAGE_KEY = 'blimo.sidebar.collapsed';

/** @type {import('react').Context<null | { collapsed: boolean, setCollapsed: (v: boolean) => void, mobileOpen: boolean, setMobileOpen: (v: boolean) => void, isDesktop: boolean, toggle: () => void }>} */
const SidebarContext = createContext(null);

export function useSidebar() {
  const ctx = use(SidebarContext);
  if (!ctx) throw new Error('useSidebar() must be used inside <SidebarProvider>');
  return ctx;
}

function readCollapsed() {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {{ children: import('react').ReactNode, className?: string, desktopQuery?: string }} props
 *   desktopQuery: from this media query on the sidebar is a column, below it an off-canvas sheet (default md = 768 px)
 */
export function SidebarProvider({ children, className, desktopQuery = '(min-width: 768px)' }) {
  const isDesktop = useMediaQuery(desktopQuery, true);
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const setCollapsed = useCallback((value) => {
    setCollapsedState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    } catch {
      // storage unavailable – ignore
    }
  }, []);

  const toggle = useCallback(() => {
    if (isDesktop) setCollapsed(!collapsed);
    else setMobileOpen((open) => !open);
  }, [isDesktop, collapsed, setCollapsed]);

  const value = useMemo(
    () => ({ collapsed: isDesktop && collapsed, setCollapsed, mobileOpen, setMobileOpen, isDesktop, toggle }),
    [isDesktop, collapsed, setCollapsed, mobileOpen, toggle],
  );

  return (
    <SidebarContext value={value}>
      <div data-slot="sidebar-wrapper" className={cn('flex min-h-dvh w-full', className)}>
        {children}
      </div>
    </SidebarContext>
  );
}

/**
 * Desktop: sticky, collapsible column. Mobile: off-canvas sheet.
 * @param {{ children: import('react').ReactNode, className?: string, label?: string, mobile?: boolean }} props
 *   mobile=false → nothing rendered below md (e.g. the portal uses a bottom navigation instead)
 */
export function Sidebar({ children, className, label = 'Hauptnavigation', mobile = true }) {
  const { collapsed, isDesktop, mobileOpen, setMobileOpen } = useSidebar();
  if (!isDesktop) {
    if (!mobile) return null;
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 gap-0 bg-sidebar p-0 text-sidebar-foreground">
          <SheetTitle className="sr-only">{label}</SheetTitle>
          <SheetDescription className="sr-only">Navigation</SheetDescription>
          <nav aria-label={label} className="flex h-full flex-col" data-collapsed="false">
            {children}
          </nav>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <aside
      data-slot="sidebar"
      data-collapsed={collapsed}
      className={cn(
        'group/sidebar sticky top-0 hidden h-dvh shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground transition-[width] duration-200 md:flex',
        collapsed ? 'w-(--sidebar-width-collapsed)' : 'w-(--sidebar-width)',
        className,
      )}
    >
      <nav aria-label={label} className="flex h-full flex-col overflow-hidden">
        {children}
      </nav>
    </aside>
  );
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SidebarHeader({ className, ...props }) {
  return <div data-slot="sidebar-header" className={cn('flex h-14 shrink-0 items-center gap-2 border-b px-3', className)} {...props} />;
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SidebarContent({ className, ...props }) {
  return <div data-slot="sidebar-content" className={cn('flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden py-2', className)} {...props} />;
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SidebarFooter({ className, ...props }) {
  return <div data-slot="sidebar-footer" className={cn('flex shrink-0 flex-col gap-1 border-t p-2', className)} {...props} />;
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SidebarGroup({ className, ...props }) {
  return <div data-slot="sidebar-group" role="group" className={cn('flex flex-col px-2 py-1', className)} {...props} />;
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SidebarGroupLabel({ className, ...props }) {
  return (
    <div
      data-slot="sidebar-group-label"
      className={cn(
        'px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground group-data-[collapsed=true]/sidebar:sr-only',
        className,
      )}
      {...props}
    />
  );
}

/** @param {import('react').ComponentProps<'ul'>} props */
export function SidebarMenu({ className, ...props }) {
  return <ul data-slot="sidebar-menu" className={cn('flex flex-col gap-0.5', className)} {...props} />;
}

/** @param {import('react').ComponentProps<'li'>} props */
export function SidebarMenuItem({ className, ...props }) {
  return <li data-slot="sidebar-menu-item" className={cn('relative', className)} {...props} />;
}

/**
 * Menu entry. Use `asChild` to render a router <Link>. When collapsed, the label is shown as tooltip.
 * @param {import('react').ComponentProps<'button'> & { asChild?: boolean, isActive?: boolean, tooltip?: string }} props
 */
export function SidebarMenuButton({ asChild = false, isActive = false, tooltip, className, ...props }) {
  const { collapsed, isDesktop, setMobileOpen } = useSidebar();
  const Comp = asChild ? Slot.Root : 'button';
  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-active={isActive}
      aria-current={isActive ? 'page' : undefined}
      onClick={!isDesktop ? () => setMobileOpen(false) : undefined}
      className={cn(
        'flex h-(--control-height) w-full items-center gap-2.5 overflow-hidden rounded-md px-2 text-left text-sm outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-ring data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground [&>span]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
        collapsed && 'justify-center px-0 [&>span]:sr-only',
        className,
      )}
      {...props}
    />
  );
  if (!collapsed || !tooltip) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** @param {import('react').ComponentProps<typeof Button>} props */
export function SidebarTrigger({ className, ...props }) {
  const { toggle, collapsed, isDesktop, mobileOpen } = useSidebar();
  const expanded = isDesktop ? !collapsed : mobileOpen;
  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onClick={toggle}
      aria-label={expanded ? 'Navigation einklappen' : 'Navigation ausklappen'}
      aria-expanded={expanded}
      {...props}
    >
      <PanelLeft />
    </Button>
  );
}

/** Main content column next to the sidebar. @param {import('react').ComponentProps<'div'>} props */
export function SidebarInset({ className, ...props }) {
  return <div data-slot="sidebar-inset" className={cn('flex min-w-0 flex-1 flex-col', className)} {...props} />;
}
